import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { ControllAppService } from '../../../../../services/controllApp.service';
import { AuthService } from '../../../../../services/auth.service';

interface Empresa {
  empresaId: string;
  nome: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string; // Novo campo
  uf: string;
  estado: string;
  endereco: string;
  ativo: boolean;
}


@Component({
  selector: 'app-empresas',
  templateUrl: './empresas.component.html',
  styleUrls: ['./empresas.component.css'],
  standalone: false,

})
export class EmpresasComponent implements OnInit {
  formulario: FormGroup;
  empresas: Empresa[] = [];
  editando = false;
  empresaEditandoId: string | null = null;
  mensagem = '';
  isError = false; // Nova variável para controlar o tipo de alerta
  showForm: boolean = false; // Controla a exibição do formulário

  // Pagination
  currentPage = 1;
  itemsPerPage = 5;
  totalPages = 1;
  pages: number[] = [];
  paginatedClientes: Empresa[] = [];

  // Adicione estas novas propriedades
  searchTerm: string = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  role: string = ''; // Add role property


  constructor(
    private formBuilder: FormBuilder,
    private controllAppService: ControllAppService,
  
    private authService: AuthService
  ) {
    this.formulario = this.formBuilder.group({
      nome: ['', [Validators.required]],
      cep: ['', [Validators.required, Validators.pattern(/^\d{5}-?\d{3}$/)]],
      logradouro: ['', [Validators.required]],
      numero: ['', [Validators.required]],
      complemento: [''],
      bairro: ['', [Validators.required]],
      cidade: ['', [Validators.required]], // Novo campo com validação
      estado: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    
    this.verificarPermissoes();
    this.loadEmpresas();
  }

  // Method to verify user permissions
  verificarPermissoes(): void {
    // Try multiple possible locations for role information
    const roleFromDirect = localStorage.getItem('role');
    const userDataString = localStorage.getItem('userData');
    const usuarioString = localStorage.getItem('usuario');
    
    
    // Initialize role as empty string
    this.role = '';
    
    // Try to get role from direct storage
    if (roleFromDirect) {
      this.role = roleFromDirect.trim();
    } 
    // Try to get from userData if direct role not found
    else if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        if (userData && userData.role) {
          this.role = userData.role.trim();
        }
      } catch (error) {
        console.error('Error parsing userData:', error);
      }
    }
    // Try to get from usuario if still not found
    else if (usuarioString) {
      try {
        const usuario = JSON.parse(usuarioString);
        if (usuario && usuario.role) {
          this.role = usuario.role.trim();
        } else if (usuario && usuario.perfil) {
          this.role = usuario.perfil.trim();
        }
      } catch (error) {
        console.error('Error parsing usuario:', error);
      }
    }

  }
  
  // Helper method to check if user is SuperAdministrador
  isSuperAdministrador(): boolean {
    // Case-insensitive check for "SuperAdministrador" role
    return this.role.toLowerCase() === 'superadministrador';
  }

  get f() {
    return this.formulario.controls;
  }

  async buscarCep() {
    const cep = this.f['cep'].value.replace(/\D/g, '');
    if (cep.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        this.f['cep'].setErrors({ invalidCep: true });
        this.mensagem = 'CEP não encontrado. Verifique o CEP informado.';
        this.isError = true;
        return;
      }

      this.formulario.patchValue({
        logradouro: data.logradouro,
        bairro: data.bairro,
        cidade: data.localidade,
        estado: data.uf // Preenche automaticamente o campo estado com a UF
      });
      this.mensagem = 'CEP encontrado com sucesso!';
      this.isError = false;
    } catch (error: any) {
      this.mensagem = 'Erro ao buscar o CEP. Verifique sua conexão ou tente novamente mais tarde.';
      this.isError = true;
    }
  }



  onSubmit() {
    if (this.formulario.valid) {
      const formData = this.formulario.value;
      const endereco = `${formData.logradouro}, ${formData.numero}${formData.complemento ? ` - ${formData.complemento}` : ''}, ${formData.bairro}, ${formData.estado} - ${formData.uf}`;

      if (this.editando && this.empresaEditandoId !== null) {

        this.atualizarEmpresa(formData, endereco);
      } else {
 
        this.cadastrarEmpresa(formData);
      }
    } else {
      this.mensagem = 'Por favor, preencha todos os campos corretamente.';
      this.isError = true;
    }
  }

  // Função auxiliar para formatar endereço consistentemente
  formatarEndereco(endereco: any): string {
    // Verifica se temos dados de endereço
    if (!endereco) {
      return 'Endereço não disponível';
    }

    // Extrai os campos com segurança
    const logradouro = endereco.logradouro || '';
    const numero = endereco.numero || '';
    const complemento = endereco.complemento ? ` - ${endereco.complemento}` : '';
    const bairro = endereco.bairro || '';
    const localidade = endereco.localidade || '';
    const uf = endereco.uf || '';
    
    // Se não temos nenhuma informação de endereço, retorna mensagem padrão
    if (!logradouro && !bairro && !localidade && !uf) {
      return 'Endereço não disponível';
    }
    
    // Monta o endereço com as partes que existem
    let enderecoFormatado = '';
    
    if (logradouro) {
      enderecoFormatado += `${logradouro}`;
      if (numero) enderecoFormatado += `, ${numero}`;
      if (complemento) enderecoFormatado += complemento;
    }
    
    if (bairro) {
      if (enderecoFormatado) enderecoFormatado += ', ';
      enderecoFormatado += bairro;
    }
    
    if (localidade || uf) {
      if (enderecoFormatado) enderecoFormatado += ', ';
      if (localidade) enderecoFormatado += localidade;
      if (uf) enderecoFormatado += ` - ${uf}`;
    }
    
    return enderecoFormatado || 'Endereço não disponível';
  }

  cadastrarEmpresa(formData: any) {
    const empresaData = {
      nomeDaEmpresa: formData.nome,
      endereco: {
        cep: formData.cep,
        numero: formData.numero,
        complemento: formData.complemento || '',
        logradouro: formData.logradouro,
        bairro: formData.bairro,
        cidade: formData.cidade,
        estado: formData.estado // Enviar como "estado" no formato esperado pela API
      }
    };

    this.controllAppService.cadastrarEmpresa(empresaData).subscribe({
      next: (response) => {
        const novaEmpresa: Empresa = {
          empresaId: response.empresaId,
          ativo: response.ativo || true,
          ...formData,
          endereco: this.formatarEndereco({
            logradouro: formData.logradouro,
            numero: formData.numero,
            complemento: formData.complemento,
            bairro: formData.bairro,
            cidade: formData.cidade,
            estado: formData.estado
          })
        };
        this.empresas.push(novaEmpresa);
        this.resetForm();
        this.updatePagination();
        this.mensagem = 'Empresa cadastrada com sucesso!';
        this.isError = false;
      },
      error: (error: any) => {
        this.mensagem = 'Erro ao cadastrar empresa. ' + (error.message || 'Tente novamente mais tarde.');
        this.isError = true;
      }
    });
  }

  atualizarEmpresa(formData: any, endereco: string) {
    if (!this.empresaEditandoId) {
      console.error('Erro: empresaEditandoId é undefined. Não é possível atualizar.');
      this.mensagem = 'Erro: Não foi possível identificar a empresa para atualização.';
      this.isError = true;
      return;
    }

    const empresaData = {
      nomeDaEmpresa: formData.nome,
      endereco: {
        cep: formData.cep,
        numero: formData.numero,
        complemento: formData.complemento || '',
        logradouro: formData.logradouro,
        bairro: formData.bairro,
        cidade: formData.cidade,
        estado: formData.estado // Enviar como "estado" no formato esperado pela API
      }
    };

    this.controllAppService.atualizarEmpresa(this.empresaEditandoId, empresaData).subscribe({
      next: (response) => {
        const index = this.empresas.findIndex(e => e.empresaId === this.empresaEditandoId);
        if (index !== -1) {
          this.empresas[index] = {
            empresaId: this.empresaEditandoId!,
            ativo: true,
            nome: formData.nome,
            cep: formData.cep,
            logradouro: formData.logradouro,
            numero: formData.numero,
            complemento: formData.complemento || '',
            bairro: formData.bairro,
            cidade: formData.cidade,
            uf: formData.estado, // Atualiza o campo UF com o valor de estado
            estado: formData.estado,
            endereco: `${formData.logradouro}, ${formData.numero}${formData.complemento ? ` - ${formData.complemento}` : ''}, ${formData.bairro}, ${formData.cidade}, ${formData.estado}`
          };
          this.updatePagination();
          this.mensagem = 'Empresa atualizada com sucesso!';
          this.isError = false;
          this.resetForm();
        }
      },
      error: (error) => {
        console.error('Erro ao atualizar empresa:', error);
        this.mensagem = `Erro ao atualizar empresa: ${error.message || 'Tente novamente mais tarde.'}`;
        this.isError = true;
      }
    });
  }

  editarEmpresa(empresa: Empresa) {
    if (!empresa || !empresa.empresaId) {
      console.error('Erro: Empresa ou ID da empresa indefinido');
      this.mensagem = 'Erro: Não foi possível identificar a empresa';
      this.isError = true;
      return;
    }

    this.editando = true;
    this.empresaEditandoId = empresa.empresaId;
    
    // Preenche o formulário com os dados disponíveis da empresa selecionada
    this.formulario.patchValue({
      nome: empresa.nome || '',
      cep: empresa.cep || '',
      logradouro: empresa.logradouro || '',
      numero: empresa.numero || '',
      complemento: empresa.complemento || '',
      bairro: empresa.bairro || '',
      cidade: empresa.cidade || '',
      estado: empresa.uf || '' // Corrigido para usar o valor correto de UF
    });

    // Busca dados atualizados da empresa a partir da API
    this.controllAppService.buscarEmpresasPorId(this.empresaEditandoId).subscribe({
      next: (response) => {
        if (!response) {
          return; // Mantenha os dados que já foram preenchidos
        }
        
        // Apenas atualiza campos que existirem na resposta
        const updateData: any = {};
        
        if (response.nomeDaEmpresa) updateData.nome = response.nomeDaEmpresa;
        
        if (response.endereco) {
          if (response.endereco.cep) updateData.cep = response.endereco.cep;
          if (response.endereco.logradouro) updateData.logradouro = response.endereco.logradouro;
          if (response.endereco.numero) updateData.numero = response.endereco.numero;
          if (response.endereco.complemento !== undefined) updateData.complemento = response.endereco.complemento;
          if (response.endereco.bairro) updateData.bairro = response.endereco.bairro;
          if (response.endereco.uf) updateData.estado = response.endereco.uf; // Corrigido para preencher o estado com UF
          if (response.endereco.cidade) updateData.cidade = response.endereco.cidade;
        }
        this.formulario.patchValue(updateData);
      },
      error: (error) => {
        console.error('Erro ao buscar empresa:', error);
        // Não exibe mensagem de erro, pois já temos dados básicos preenchidos
      }
    });

    this.rolarParaTopo();
  }

  excluirEmpresa(empresaId: string) {
    if (confirm('Tem certeza que deseja excluir esta empresa?')) {
      this.controllAppService.deletarEmpresa(empresaId).subscribe({
        next: () => {
          this.empresas = this.empresas.filter(e => e.empresaId !== empresaId);
          this.updatePagination();
          this.mensagem = 'Empresa excluída com sucesso!';
          this.isError = false;
        },
        error: (error: any) => {
          if (error.status !== 200) {
            console.error('Erro ao excluir empresa:', error);
            this.mensagem = 'Erro ao excluir empresa. ' + (error.message || 'Tente novamente mais tarde.');
            this.isError = true;
          } else {
            this.empresas = this.empresas.filter(e => e.empresaId !== empresaId);
            this.updatePagination();
            this.mensagem = 'Empresa excluída com sucesso!';
            this.isError = false;
          }
        }
      });
    }
  }

  resetForm() {
    this.formulario.reset();
    this.editando = false;
    this.empresaEditandoId = null;
    this.mensagem = '';
    this.isError = false;
  }

  // Pagination methods
  updatePagination() {
    this.totalPages = Math.ceil(this.empresas.length / this.itemsPerPage);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.updatePaginatedEmpresas();
  }

  updatePaginatedEmpresas() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedClientes = this.empresas.slice(startIndex, endIndex);
  }

  onItemsPerPageChange() {
    this.currentPage = 1;
    this.updatePagination();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedEmpresas();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedEmpresas();
    }
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.updatePaginatedEmpresas();
  }

  rolarParaTopo() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadEmpresas() {
    this.controllAppService.buscarEmpresas().subscribe({
      next: (response) => {

        this.empresas = response.map((empresa: any) => {
          // Dados de endereço diretos da API
          const endereco = empresa.endereco;
          
          // Criar formato de endereço manualmente, sem usar funções auxiliares
          let enderecoFinal = '';
          
          try {
            // Lógica direta e simplificada - tenta todas as possibilidades
            if (endereco) {
              // Concatenar logradouro e número (mesmo que apenas um exista)
              const temLogradouro = endereco.logradouro && endereco.logradouro.trim() !== '';
              const temNumero = endereco.numero && endereco.numero.trim() !== '';
              
              if (temLogradouro || temNumero) {
                if (temLogradouro) enderecoFinal += endereco.logradouro;
                if (temNumero) enderecoFinal += temLogradouro ? `, ${endereco.numero}` : endereco.numero;
              }
              
              // Adicionar complemento se existir
              if (endereco.complemento && endereco.complemento.trim() !== '') {
                enderecoFinal += ` - ${endereco.complemento}`;
              }
              
              // Adicionar bairro se existir
              if (endereco.bairro && endereco.bairro.trim() !== '') {
                if (enderecoFinal) enderecoFinal += ', ';
                enderecoFinal += endereco.bairro;
              }
              
             
              const temLocalidade = endereco.localidade && endereco.localidade.trim() !== '';
              const temUF = endereco.uf && endereco.uf.trim() !== '';
              
              if (temLocalidade || temUF) {
                if (enderecoFinal) enderecoFinal += ', ';
                if (temLocalidade) enderecoFinal += endereco.localidade;
                if (temUF) enderecoFinal += temLocalidade ? ` - ${endereco.uf}` : endereco.uf;
              }
            }
            
           
            if (!enderecoFinal) {
              
              const campos = ['logradouro', 'numero', 'bairro', 'estado', 'uf'];
              const partes = [];
              
              for (const campo of campos) {
                if (empresa[campo] && empresa[campo].trim() !== '') {
                  partes.push(empresa[campo]);
                }
              }
              
              enderecoFinal = partes.join(', ');
            }
            
            // Último recurso
            if (!enderecoFinal && typeof endereco === 'string') {
              enderecoFinal = endereco;
            }
          } catch (error) {
            console.error('Erro ao montar endereço:', error);
          }
          
          // Se depois de tudo, ainda não temos um endereço, usamos um texto padrão
          if (!enderecoFinal) {
            enderecoFinal = 'Rua ' + (empresa.logradouro || '') + 
                            ', ' + (empresa.numero || 'S/N') + 
                            (empresa.complemento ? ' - ' + empresa.complemento : '') + 
                            ', ' + (empresa.bairro || 'Centro') + 
                            ', ' + (empresa.estado || '') + 
                            ' - ' + (empresa.uf || '');
          }
          
          return {
            empresaId: empresa.empresaId,
            ativo: empresa.ativo || true,
            nome: empresa.nomeDaEmpresa || '',
            cep: empresa.endereco?.cep || '',
            logradouro: empresa.endereco?.logradouro || '',
            numero: empresa.endereco?.numero || '',
            complemento: empresa.endereco?.complemento || '',
            bairro: empresa.endereco?.bairro || '',
            cidade: empresa.endereco?.cidade || empresa.endereco?.localidade || '', 
            uf: empresa.endereco?.uf || '',
            estado: empresa.endereco?.localidade || '',
            endereco: enderecoFinal || 'Endereço não informado'
          };
        });
        
        this.updatePagination();
      },
      error: (error: any) => {
        this.mensagem = 'Erro ao carregar a lista de empresas. ' + (error.message || 'Verifique sua conexão e tente novamente.');
        this.isError = true;
      }
    });
  }

  // Adicione estes novos métodos
  onSearch() {
    this.currentPage = 1;
    this.filterAndSortEmpresas();
  }

  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.filterAndSortEmpresas();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  filterAndSortEmpresas() {
    let filteredEmpresas = [...this.empresas];
    
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filteredEmpresas = filteredEmpresas.filter(empresa => 
        empresa.nome.toLowerCase().includes(search) ||
        empresa.cidade.toLowerCase().includes(search) ||
        empresa.uf.toLowerCase().includes(search) ||
        empresa.endereco.toLowerCase().includes(search)
      );
    }

    if (this.sortColumn) {
      filteredEmpresas.sort((a, b) => {
        const aValue = a[this.sortColumn as keyof Empresa];
        const bValue = b[this.sortColumn as keyof Empresa];
        
        if (this.sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    this.empresas = filteredEmpresas;
    this.updatePagination();
  }

  getVisiblePages(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const maxVisible = 5;

    if (total <= maxVisible) {
      return Array.from({length: total}, (_, i) => i + 1);
    }

    if (current <= 3) {
      return [1, 2, 3, 4, 5];
    }

    if (current >= total - 2) {
      return Array.from({length: 5}, (_, i) => total - 4 + i);
    }

    return [current - 2, current - 1, current, current + 1, current + 2];
  }

  getPaginationInfo() {
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(start + this.itemsPerPage - 1, this.empresas.length);
    return { start, end };
  }

  toggleForm() {
    this.showForm = !this.showForm;
    if (!this.showForm) {
      this.resetForm(); // Reseta o formulário ao voltar para a tabela
    }
  }
}